import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faCalendarCheck,
  faCarSide,
  faClock,
  faHandHoldingHeart,
  faHospital,
  faLocationDot,
  faPhone,
  faShieldAlt,
  faWallet,
  faWheelchair
} from '@fortawesome/free-solid-svg-icons';
import Banner from './Banner';
import seoPages from '../data/seo-pages.json';
import '../styles/TransportSeoPages.css';

const iconByName = {
  calendar: faCalendarCheck,
  car: faCarSide,
  care: faHandHoldingHeart,
  hospital: faHospital,
  location: faLocationDot,
  shield: faShieldAlt,
  wallet: faWallet,
  wheelchair: faWheelchair
};

const pageContent = {
  '/transport-medico-sanitaire-bruxelles': {
    icon: 'car',
    related: ['/transport-hopital-bruxelles', '/transport-dialyse-bruxelles', '/transport-pmr-bruxelles'],
    fr: {
      eyebrow: 'Transport non urgent',
      title: 'Transport médico-sanitaire à Bruxelles',
      accent: 'Bruxelles',
      subtitle: 'Un trajet planifié pour rejoindre un hôpital, une clinique, un centre médical ou un autre lieu de soins, avec une présence attentive du départ à l’arrivée.',
      introTitle: 'Un VSL pour vos rendez-vous médicaux non urgents',
      intro: [
        'Main Tendue organise le transport de patients assis lorsque la situation ne nécessite pas l’intervention d’une ambulance urgente. La prise en charge peut commencer au domicile, dans une résidence ou une maison de repos.',
        'L’horaire, les adresses, le service de destination et les besoins d’accompagnement sont vérifiés lors de la réservation. Cette préparation permet d’organiser un transport médical à Bruxelles adapté au rendez-vous prévu.'
      ],
      highlights: [
        ['Trajet préparé', 'Les heures de prise en charge, de rendez-vous et de retour sont organisées avant la course.'],
        ['Accompagnement humain', 'Le passager bénéficie d’une aide porte à porte selon son niveau d’autonomie.'],
        ['Bruxelles et alentours', 'Les départs et arrivées sont organisés dans les 19 communes et, sur demande, au-delà.']
      ],
      usesTitle: 'Pour quels déplacements ?',
      usesIntro: 'Le véhicule sanitaire léger convient à de nombreux déplacements programmés qui ne relèvent pas de l’urgence médicale.',
      uses: ['Consultation chez un médecin ou un spécialiste', 'Rendez-vous dans une clinique ou une polyclinique', 'Examen, scanner, IRM ou analyse en laboratoire', 'Soins ambulatoires et centre médical', 'Admission ou sortie planifiée d’un établissement', 'Trajet simple ou aller-retour organisé'],
      detailTitle: 'Une réservation adaptée à la situation du patient',
      detail: 'Indiquez lors de la demande la mobilité du passager, la présence d’un accompagnant, les horaires connus et les contraintes d’accès. Si un fauteuil roulant ou une assistance particulière est nécessaire, nous vérifions la solution adaptée avant de confirmer le transport.',
      faq: [
        ['Quelle différence avec une ambulance urgente ?', 'Ce service concerne les déplacements planifiés et non urgents. En cas d’urgence médicale, il faut contacter les services d’urgence.'],
        ['Peut-on réserver un aller-retour ?', 'Oui. L’aller et le retour peuvent être communiqués dès la réservation, y compris lorsque l’heure de fin est estimée.'],
        ['Le transport peut-il partir d’une maison de repos ?', 'Oui. Le point de départ peut être un domicile, une résidence, une maison de repos ou un établissement de soins.']
      ]
    },
    nl: {
      eyebrow: 'Niet-dringend vervoer',
      title: 'Medisch vervoer in Brussel',
      accent: 'Brussel',
      subtitle: 'Planbaar vervoer naar een ziekenhuis, kliniek of zorgcentrum, met persoonlijke begeleiding van vertrek tot aankomst.',
      introTitle: 'Patiëntenvervoer voor geplande zorg',
      intro: ['Main Tendue organiseert zittend patiëntenvervoer wanneer geen dringende ambulance nodig is.', 'Bij de reservatie controleren we de uren, adressen, bestemming en de gewenste begeleiding.'],
      highlights: [['Geplande rit', 'Ophaling, afspraak en terugrit worden vooraf afgestemd.'], ['Persoonlijke hulp', 'De passagier krijgt hulp van deur tot deur volgens zijn autonomie.'], ['Heel Brussel', 'Vervoer in de 19 Brusselse gemeenten en op aanvraag daarbuiten.']],
      usesTitle: 'Voor welke verplaatsingen?',
      usesIntro: 'Voor raadplegingen, onderzoeken en andere niet-dringende zorgafspraken.',
      uses: ['Arts of specialist', 'Ziekenhuis of kliniek', 'Medisch onderzoek', 'Ambulante zorg', 'Geplande opname of ontslag', 'Enkele rit of heen-en-terug'],
      detailTitle: 'Een reservatie op maat',
      detail: 'Vermeld de mobiliteit van de passagier, de gekende uren en eventuele hulpvragen. Zo kunnen we de geschikte oplossing vooraf bevestigen.',
      faq: [['Is dit dringend ziekenvervoer?', 'Nee. Bij een medische noodsituatie moet u de hulpdiensten contacteren.'], ['Kan ik een terugrit boeken?', 'Ja, een heen- en terugrit kan samen worden aangevraagd.'], ['Kan de rit aan een woonzorgcentrum starten?', 'Ja, thuis, een residentie of een zorginstelling kunnen als vertrekpunt dienen.']]
    }
  },
  '/transport-pmr-bruxelles': {
    icon: 'wheelchair',
    related: ['/transport-medico-sanitaire-bruxelles', '/transport-seniors-bruxelles', '/transport-medical-anderlecht'],
    fr: {
      eyebrow: 'Mobilité accessible',
      title: 'Transport PMR et TPMR à Bruxelles',
      accent: 'TPMR',
      subtitle: 'Des véhicules adaptés et une aide porte à porte pour les personnes à mobilité réduite ou se déplaçant en fauteuil roulant.',
      introTitle: 'Se déplacer sans quitter son fauteuil roulant',
      intro: [
        'Le service TPMR de Main Tendue est destiné aux personnes dont la mobilité rend un véhicule classique difficile ou impossible à utiliser. La prise en charge tient compte du fauteuil, de l’accès au bâtiment et de l’aide nécessaire.',
        'Un fauteuil manuel ou électrique n’a pas les mêmes dimensions ni les mêmes contraintes. Nous demandons donc ces informations avant la course afin de vérifier la compatibilité du véhicule et d’éviter une difficulté le jour du trajet.'
      ],
      highlights: [
        ['Véhicule adapté', 'Accès prévu pour le transport en fauteuil roulant, après vérification des dimensions et du besoin.'],
        ['Aide à la mobilité', 'Assistance à la montée, à la descente et entre la porte et le véhicule.'],
        ['Trajet sécurisé', 'Le fauteuil et le passager sont installés selon les équipements prévus dans le véhicule.']
      ],
      usesTitle: 'Une solution pour chaque contexte de mobilité',
      usesIntro: 'Le transport PMR peut être demandé pour un rendez-vous médical comme pour un déplacement essentiel non médical.',
      uses: ['Fauteuil roulant manuel', 'Fauteuil roulant électrique, après vérification', 'Personne marchant avec difficulté', 'Départ depuis un étage ou une résidence, selon l’accessibilité', 'Accompagnement vers l’accueil du lieu de destination', 'Transport sur réservation dans Bruxelles'],
      detailTitle: 'Les informations à communiquer avant la course',
      detail: 'Précisez le type de fauteuil, ses dimensions particulières, la présence de marches ou d’un ascenseur et la capacité de la personne à effectuer un transfert. Ces éléments permettent à l’équipe de confirmer le véhicule et l’accompagnement adéquats.',
      faq: [
        ['Transportez-vous les fauteuils électriques ?', 'Oui lorsque leurs dimensions et leur poids sont compatibles avec le véhicule disponible. Une vérification est réalisée à la réservation.'],
        ['Aidez-vous la personne entre son domicile et le véhicule ?', 'L’accompagnement porte à porte fait partie de l’organisation prévue, dans les limites d’accessibilité communiquées.'],
        ['Un proche peut-il accompagner le passager ?', 'Signalez sa présence lors de la réservation afin de confirmer la place disponible.']
      ]
    },
    nl: {
      eyebrow: 'Toegankelijke mobiliteit',
      title: 'Aangepast PMR-vervoer in Brussel',
      accent: 'PMR-vervoer',
      subtitle: 'Aangepaste voertuigen en deur-tot-deurhulp voor personen met beperkte mobiliteit en rolstoelgebruikers.',
      introTitle: 'Reizen in de eigen rolstoel',
      intro: ['De dienst is bedoeld voor personen die moeilijk gebruik kunnen maken van een gewone wagen.', 'We controleren vooraf het type rolstoel, de toegang tot het gebouw en de nodige hulp.'],
      highlights: [['Aangepast voertuig', 'Rolstoeltoegang na controle van afmetingen en behoeften.'], ['Mobiliteitshulp', 'Hulp bij het in- en uitstappen en tussen deur en voertuig.'], ['Veilige installatie', 'Passagier en rolstoel worden met de voorziene uitrusting vastgezet.']],
      usesTitle: 'Voor verschillende mobiliteitsbehoeften',
      usesIntro: 'Voor medische afspraken en andere essentiële verplaatsingen.',
      uses: ['Manuele rolstoel', 'Elektrische rolstoel na controle', 'Moeilijk stappen', 'Vertrek aan woning of residentie', 'Begeleiding tot de receptie', 'Vervoer op reservatie'],
      detailTitle: 'Wat vooraf melden?',
      detail: 'Vermeld het type rolstoel, bijzondere afmetingen, trappen of lift en de mogelijkheid om over te stappen.',
      faq: [['Zijn elektrische rolstoelen mogelijk?', 'Ja, na controle van afmetingen, gewicht en beschikbaar voertuig.'], ['Is er deur-tot-deurhulp?', 'Ja, binnen de vooraf gemelde toegankelijkheidsvoorwaarden.'], ['Kan een begeleider mee?', 'Meld dit bij de reservatie zodat de beschikbare plaats kan worden bevestigd.']]
    }
  },
  '/transport-dialyse-bruxelles': {
    icon: 'calendar',
    related: ['/transport-medico-sanitaire-bruxelles', '/transport-hopital-bruxelles', '/remboursement-transport-medical'],
    fr: {
      eyebrow: 'Soins réguliers',
      title: 'Transport dialyse à Bruxelles',
      accent: 'dialyse',
      subtitle: 'Une organisation stable pour les séances récurrentes, avec prise en charge, arrivée au centre et retour planifié.',
      introTitle: 'Des trajets récurrents organisés autour des soins',
      intro: [
        'Les séances de dialyse imposent des déplacements fréquents et des horaires précis. Main Tendue peut préparer un planning de transports récurrents afin de limiter les démarches répétitives pour le patient, sa famille ou l’établissement qui l’accompagne.',
        'Les jours de traitement, l’heure souhaitée d’arrivée et le fonctionnement du retour sont définis avant le début de la série. Toute modification communiquée par le centre peut ensuite être intégrée à l’organisation.'
      ],
      highlights: [
        ['Planning récurrent', 'Les jours et horaires réguliers peuvent être enregistrés pour faciliter l’organisation.'],
        ['Contact coordonné', 'La famille, la résidence ou le centre peut être identifié comme contact de suivi.'],
        ['Retour anticipé', 'Le principe du retour est préparé dès la demande, même si l’heure doit être confirmée.']
      ],
      usesTitle: 'Au-delà de la dialyse',
      usesIntro: 'La même organisation peut convenir à d’autres soins réguliers, sous réserve des besoins du patient et des disponibilités.',
      uses: ['Hémodialyse', 'Rééducation ou kinésithérapie', 'Radiothérapie', 'Chimiothérapie et suivi en oncologie', 'Centre de revalidation', 'Autres soins ambulatoires réguliers'],
      detailTitle: 'Préparer une série de transports',
      detail: 'Communiquez les dates, la fréquence, l’heure d’arrivée demandée, la durée habituelle et les coordonnées du service. Nous confirmons ensuite les trajets réalisables et les modalités de retour. Une demande ne vaut réservation qu’après confirmation par Main Tendue.',
      faq: [
        ['Peut-on réserver plusieurs semaines en une fois ?', 'Oui. Un planning récurrent peut être préparé à partir des dates communiquées, puis confirmé selon les disponibilités.'],
        ['Que faire si le centre modifie un horaire ?', 'Prévenez Main Tendue dès que possible afin que l’équipe vérifie l’adaptation du trajet.'],
        ['Le retour est-il compris automatiquement ?', 'L’aller-retour doit être demandé explicitement. Les modalités du retour sont alors définies lors de la réservation.']
      ]
    },
    nl: {
      eyebrow: 'Regelmatige zorg',
      title: 'Dialysevervoer in Brussel',
      accent: 'Dialysevervoer',
      subtitle: 'Een stabiele planning voor terugkerende behandelingen, met georganiseerde heen- en terugrit.',
      introTitle: 'Terugkerende ritten rond de behandeling',
      intro: ['Dialyse vraagt frequente verplaatsingen op vaste uren.', 'Een terugkerende planning beperkt herhaalde administratie voor patiënt, familie of instelling.'],
      highlights: [['Vaste planning', 'Regelmatige dagen en uren kunnen vooraf worden ingepland.'], ['Gecoördineerd contact', 'Familie, residentie of centrum kan als contact worden aangeduid.'], ['Voorziene terugrit', 'De terugrit wordt vanaf de aanvraag besproken.']],
      usesTitle: 'Ook voor andere regelmatige zorg',
      usesIntro: 'Afhankelijk van behoeften en beschikbaarheid.',
      uses: ['Hemodialyse', 'Revalidatie of kinesitherapie', 'Radiotherapie', 'Oncologische opvolging', 'Revalidatiecentrum', 'Andere ambulante zorg'],
      detailTitle: 'Een reeks ritten voorbereiden',
      detail: 'Bezorg data, frequentie, gewenst aankomstuur, gebruikelijke duur en contactgegevens van de dienst.',
      faq: [['Kan ik meerdere weken boeken?', 'Ja, een terugkerende planning kan worden voorbereid en volgens beschikbaarheid bevestigd.'], ['Wat bij een uurwijziging?', 'Verwittig Main Tendue zo snel mogelijk.'], ['Is de terugrit automatisch inbegrepen?', 'Nee, vraag de heen- en terugrit uitdrukkelijk aan.']]
    }
  },
  '/transport-hopital-bruxelles': {
    icon: 'hospital',
    related: ['/transport-medico-sanitaire-bruxelles', '/transport-pmr-bruxelles', '/transport-dialyse-bruxelles'],
    fr: {
      eyebrow: 'Domicile et hôpital',
      title: 'Transport vers l’hôpital à Bruxelles',
      accent: 'hôpital',
      subtitle: 'Pour une consultation, un examen, une admission ou une sortie planifiée, avec les informations utiles transmises avant le départ.',
      introTitle: 'Arriver au bon endroit, à l’heure prévue',
      intro: [
        'Un rendez-vous hospitalier ne se limite pas à une adresse. Le site, l’entrée, le service, l’étage et l’heure d’enregistrement peuvent être déterminants. Nous recueillons ces informations pour organiser le trajet domicile-hôpital avec précision.',
        'Pour le retour, indiquez si l’heure est fixe ou si elle dépend de la fin de la consultation. L’équipe vous explique la modalité de reprise possible avant de confirmer la course.'
      ],
      highlights: [
        ['Destination précisée', 'Hôpital, site, service et point d’accueil sont notés dans la demande.'],
        ['Marge d’arrivée', 'L’horaire de prise en charge tient compte de l’heure d’enregistrement communiquée.'],
        ['Retour organisé', 'Sortie, retour à domicile ou transfert non urgent peuvent être préparés à l’avance.']
      ],
      usesTitle: 'Les principaux trajets hospitaliers',
      usesIntro: 'Le service concerne les rendez-vous programmés ne nécessitant pas une ambulance urgente.',
      uses: ['Consultation ou rendez-vous avec un spécialiste', 'Scanner, IRM, laboratoire ou autre examen', 'Admission programmée', 'Sortie d’hôpital planifiée', 'Soins ambulatoires', 'Retour vers le domicile ou la résidence'],
      detailTitle: 'Documents et informations utiles',
      detail: 'Gardez la convocation et les coordonnées du service à portée de main. Lors de la réservation, communiquez l’heure à laquelle le patient doit être présent, plutôt que la seule heure de consultation. Mentionnez également toute aide à la mobilité.',
      faq: [
        ['Pouvez-vous venir chercher le patient dans le service ?', 'Le point de reprise doit être défini à l’avance et rester accessible au chauffeur. Indiquez précisément le bâtiment et le service.'],
        ['Que faire si la consultation se termine en retard ?', 'Contactez l’équipe dès que possible. Une nouvelle heure sera recherchée selon les courses déjà planifiées.'],
        ['Organisez-vous les sorties d’hôpital ?', 'Oui lorsqu’elles sont non urgentes, planifiées et compatibles avec les besoins de mobilité communiqués.']
      ]
    },
    nl: {
      eyebrow: 'Thuis en ziekenhuis',
      title: 'Ziekenhuisvervoer in Brussel',
      accent: 'Ziekenhuisvervoer',
      subtitle: 'Voor een raadpleging, onderzoek, opname of gepland ontslag, met alle praktische informatie vooraf.',
      introTitle: 'Op tijd aan de juiste ingang',
      intro: ['Site, ingang, dienst en registratietijd zijn belangrijk voor een ziekenhuisrit.', 'Voor de terugrit spreken we vooraf af of het uur vast of nog te bevestigen is.'],
      highlights: [['Juiste bestemming', 'Site, dienst en onthaalpunt worden genoteerd.'], ['Aankomstmarge', 'De ophaling houdt rekening met het gevraagde registratietijdstip.'], ['Geplande terugrit', 'Terugkeer naar woning of residentie kan vooraf worden voorbereid.']],
      usesTitle: 'Veel voorkomende ziekenhuisritten',
      usesIntro: 'Voor geplande afspraken zonder dringende ambulance.',
      uses: ['Raadpleging specialist', 'Scanner, MRI of labo', 'Geplande opname', 'Gepland ontslag', 'Ambulante zorg', 'Terug naar woning of residentie'],
      detailTitle: 'Nuttige informatie',
      detail: 'Bezorg het uur waarop de patiënt aanwezig moet zijn, de dienst en eventuele mobiliteitshulp.',
      faq: [['Kan de patiënt aan de dienst worden opgehaald?', 'Spreek vooraf een toegankelijk en precies ophaalpunt af.'], ['Wat bij vertraging?', 'Contacteer het team zo snel mogelijk.'], ['Doen jullie ziekenhuisontslag?', 'Ja, wanneer het niet dringend, gepland en praktisch haalbaar is.']]
    }
  },
  '/transport-seniors-bruxelles': {
    icon: 'care',
    related: ['/transport-pmr-bruxelles', '/transport-hopital-bruxelles', '/remboursement-transport-medical'],
    fr: {
      eyebrow: 'Présence humaine',
      title: 'Transport des seniors à Bruxelles',
      accent: 'seniors',
      subtitle: 'Un accompagnement rassurant depuis le domicile, une résidence service, une MRPA ou une maison de repos et de soins.',
      introTitle: 'Plus qu’un trajet entre deux adresses',
      intro: [
        'Une personne âgée peut avoir besoin de temps, de repères clairs et d’une aide concrète pour quitter son domicile ou rejoindre l’accueil d’un établissement. Le service est organisé en tenant compte de son autonomie et des informations données par la famille ou la résidence.',
        'Le chauffeur accompagnateur assure le lien prévu entre la porte, le véhicule et le point d’arrivée. Pour une personne dépendante ou fragile, les besoins précis doivent être communiqués afin de confirmer que le transport est adapté.'
      ],
      highlights: [
        ['Contact de confiance', 'Un proche ou un professionnel peut être renseigné comme contact avant et après la course.'],
        ['Rythme adapté', 'Le temps de déplacement entre la porte et le véhicule est pris en compte.'],
        ['Résidences et domicile', 'Départs possibles depuis une habitation, une résidence service, une MRPA ou une MRS.']
      ],
      usesTitle: 'Des déplacements médicaux et essentiels',
      usesIntro: 'Le transport accompagné aide à préserver la mobilité sociale et l’accès aux soins.',
      uses: ['Consultation ou suivi médical', 'Hôpital, clinique ou centre de jour', 'Maison de repos et de soins', 'Centre de revalidation', 'Démarche essentielle ou rendez-vous planifié', 'Visite familiale ou déplacement non médical, sur demande'],
      detailTitle: 'Organiser le trajet avec la famille ou l’établissement',
      detail: 'Une réservation peut être introduite par le passager, un proche, une maison de repos ou un service social. Indiquez les coordonnées de la personne à prévenir, le niveau d’aide requis et les modalités d’accueil à destination.',
      faq: [
        ['La famille peut-elle réserver pour un proche ?', 'Oui. Les coordonnées du passager et d’un contact de confiance seront demandées.'],
        ['Travaillez-vous avec les maisons de repos ?', 'Oui, des trajets peuvent être organisés au départ ou à destination d’une résidence, d’une MRPA ou d’une MRS.'],
        ['Proposez-vous aussi des trajets non médicaux ?', 'Certains déplacements essentiels ou privés peuvent être organisés sur demande selon le tarif et les disponibilités.']
      ]
    },
    nl: {
      eyebrow: 'Menselijke begeleiding',
      title: 'Seniorenvervoer in Brussel',
      accent: 'Seniorenvervoer',
      subtitle: 'Geruststellende begeleiding vanaf de woning, serviceflat of het woonzorgcentrum.',
      introTitle: 'Meer dan een rit tussen twee adressen',
      intro: ['Een oudere persoon heeft soms extra tijd, duidelijke afspraken en praktische hulp nodig.', 'We houden rekening met autonomie en de informatie van familie of residentie.'],
      highlights: [['Vertrouwenscontact', 'Een familielid of professional kan als contact worden aangeduid.'], ['Aangepast tempo', 'De verplaatsing tussen deur en voertuig wordt meegenomen.'], ['Woning en residentie', 'Vertrek aan woning, serviceflat of woonzorgcentrum.']],
      usesTitle: 'Medische en essentiële verplaatsingen',
      usesIntro: 'Begeleid vervoer ondersteunt toegang tot zorg en sociale mobiliteit.',
      uses: ['Medische raadpleging', 'Ziekenhuis of dagcentrum', 'Woonzorgcentrum', 'Revalidatiecentrum', 'Essentiële afspraak', 'Privéverplaatsing op aanvraag'],
      detailTitle: 'Organiseren met familie of instelling',
      detail: 'Vermeld een contactpersoon, de nodige hulp en de ontvangstvoorwaarden op de bestemming.',
      faq: [['Kan familie reserveren?', 'Ja, we vragen gegevens van de passagier en een contactpersoon.'], ['Werken jullie met woonzorgcentra?', 'Ja, ritten van en naar residenties kunnen worden georganiseerd.'], ['Zijn niet-medische ritten mogelijk?', 'Sommige essentiële of private ritten zijn op aanvraag mogelijk.']]
    }
  },
  '/remboursement-transport-medical': {
    icon: 'wallet',
    related: ['/transport-medico-sanitaire-bruxelles', '/transport-pmr-bruxelles', '/transport-dialyse-bruxelles'],
    fr: {
      eyebrow: 'Mutuelle et aide sociale',
      title: 'Remboursement du transport médical',
      accent: 'Remboursement',
      subtitle: 'Les interventions de la mutuelle, de Partenamut ou du CPAS dépendent de votre situation, du motif du trajet et des conditions de l’organisme.',
      introTitle: 'Vérifier l’intervention avant le transport',
      intro: [
        'Un transport médical ou PMR n’est pas automatiquement remboursé. Certaines mutualités prévoient une intervention pour des traitements ou situations déterminés, tandis qu’un CPAS peut examiner une aide selon le dossier social de la personne.',
        'Main Tendue est une ASBL agréée COCOF sous le numéro 30.01.14 et collabore notamment avec Partenamut. L’existence d’un partenariat ne garantit toutefois pas une prise en charge pour chaque course : la décision appartient toujours à l’organisme concerné.'
      ],
      highlights: [
        ['Conditions variables', 'Le montant et les trajets admissibles dépendent de la mutualité, du contrat et du motif médical.'],
        ['Documents à conserver', 'Prescription, attestation, preuve de présence et facture peuvent être demandées.'],
        ['Vérification préalable', 'Contactez votre mutualité ou votre CPAS avant le premier trajet lorsque c’est possible.']
      ],
      usesTitle: 'Les étapes à suivre',
      usesIntro: 'Préparer le dossier en amont limite les mauvaises surprises au moment de demander l’intervention.',
      uses: ['Demander les conditions exactes à la mutualité', 'Vérifier si une autorisation préalable est nécessaire', 'Faire compléter les documents médicaux demandés', 'Conserver les justificatifs de chaque déplacement', 'Transmettre la facture selon la procédure indiquée', 'Contacter le CPAS si une aide sociale doit être examinée'],
      detailTitle: 'Partenamut, mutualité et CPAS : qui décide ?',
      detail: 'Chaque organisme applique ses propres règles et peut les modifier. Pour une information définitive, consultez directement votre mutualité, Partenamut ou votre CPAS. Main Tendue peut fournir les documents relatifs au transport dont elle est responsable, mais ne peut pas promettre un remboursement.',
      faq: [
        ['Le transport est-il toujours remboursé par la mutuelle ?', 'Non. L’intervention dépend du motif, de votre couverture et des règles de votre mutualité.'],
        ['Que signifie l’agrément COCOF ?', 'Il identifie Main Tendue comme service agréé sous le numéro 30.01.14. Il ne constitue pas à lui seul une garantie de remboursement.'],
        ['Puis-je demander une aide au CPAS ?', 'Un CPAS peut examiner une demande selon la situation sociale. Contactez le CPAS compétent avant le transport lorsque cela est possible.']
      ]
    },
    nl: {
      eyebrow: 'Mutualiteit en sociale hulp',
      title: 'Terugbetaling van medisch vervoer',
      accent: 'Terugbetaling',
      subtitle: 'Een tussenkomst van mutualiteit, Partenamut of OCMW hangt af van uw situatie, de rit en hun voorwaarden.',
      introTitle: 'Controleer de tussenkomst vooraf',
      intro: ['Medisch of aangepast vervoer wordt niet automatisch terugbetaald.', 'Main Tendue is erkend door de COCOF onder nummer 30.01.14, maar de bevoegde organisatie beslist over elke tussenkomst.'],
      highlights: [['Verschillende voorwaarden', 'Bedrag en toegelaten ritten hangen af van mutualiteit en reden.'], ['Documenten bewaren', 'Voorschrift, aanwezigheidsbewijs en factuur kunnen nodig zijn.'], ['Vooraf controleren', 'Contacteer mutualiteit of OCMW voor de eerste rit.']],
      usesTitle: 'Te volgen stappen',
      usesIntro: 'Een voorbereid dossier voorkomt verrassingen.',
      uses: ['Vraag de exacte voorwaarden', 'Controleer voorafgaande toestemming', 'Laat medische documenten invullen', 'Bewaar ritbewijzen', 'Bezorg de factuur', 'Contacteer het bevoegde OCMW'],
      detailTitle: 'Wie beslist?',
      detail: 'Elke organisatie hanteert eigen regels. Main Tendue kan vervoersdocumenten bezorgen, maar geen terugbetaling beloven.',
      faq: [['Betaalt de mutualiteit altijd terug?', 'Nee, dit hangt af van dekking, reden en voorwaarden.'], ['Wat betekent de COCOF-erkenning?', 'Main Tendue is erkend onder nummer 30.01.14; dit garandeert geen terugbetaling.'], ['Kan het OCMW helpen?', 'Het bevoegde OCMW kan een aanvraag volgens de sociale situatie onderzoeken.']]
    }
  },
  '/transport-medical-anderlecht': {
    icon: 'location',
    related: ['/transport-medico-sanitaire-bruxelles', '/transport-pmr-bruxelles', '/transport-seniors-bruxelles'],
    fr: {
      eyebrow: 'Ancrage local',
      title: 'Transport médical et PMR à Anderlecht',
      accent: 'Anderlecht',
      subtitle: 'Main Tendue organise des transports sur réservation depuis Anderlecht et dans l’ensemble de la Région de Bruxelles-Capitale.',
      introTitle: 'Un service bruxellois basé à Anderlecht',
      intro: [
        'Main Tendue Pour Tous ASBL est située Route de Lennik 804/126 à 1070 Anderlecht. Cette implantation facilite l’organisation de transports médicaux non urgents, VSL, TPMR et PMR au départ ou à destination de la commune.',
        'Le service ne se limite pas à Anderlecht : les trajets peuvent concerner les 19 communes bruxelloises et des destinations extérieures, selon la demande, le type de transport et les disponibilités.'
      ],
      highlights: [
        ['Adresse à Anderlecht', 'L’équipe de coordination est établie Route de Lennik, à proximité du réseau hospitalier bruxellois.'],
        ['19 communes', 'Les demandes sont étudiées pour toute la Région de Bruxelles-Capitale.'],
        ['Sur réservation', 'Chaque trajet est confirmé après vérification des horaires, adresses et besoins de mobilité.']
      ],
      usesTitle: 'Zone desservie à Bruxelles',
      usesIntro: 'Une seule page locale regroupe la zone réellement couverte, sans créer de pages artificielles pour chaque commune.',
      uses: ['Anderlecht, Bruxelles-Ville et Molenbeek-Saint-Jean', 'Forest, Saint-Gilles, Uccle et Ixelles', 'Jette, Ganshoren, Koekelberg et Berchem-Sainte-Agathe', 'Schaerbeek, Evere, Saint-Josse-ten-Noode et Etterbeek', 'Auderghem et Watermael-Boitsfort', 'Woluwe-Saint-Lambert et Woluwe-Saint-Pierre'],
      detailTitle: 'Réserver un départ à Anderlecht',
      detail: 'Communiquez l’adresse complète, l’étage et l’accessibilité, l’heure du rendez-vous, la destination exacte et le besoin éventuel en fauteuil roulant. Pour une arrivée hors de Bruxelles, l’équipe vérifie également la distance et le tarif applicable.',
      faq: [
        ['Intervenez-vous uniquement à Anderlecht ?', 'Non. Main Tendue étudie les demandes dans les 19 communes de Bruxelles et certains trajets hors Région.'],
        ['Peut-on partir d’Anderlecht vers un hôpital hors Bruxelles ?', 'Oui sur demande. La faisabilité et le tarif sont vérifiés avant confirmation.'],
        ['Comment confirmer une course ?', 'Utilisez le formulaire de réservation ou téléphonez. La course est confirmée après validation par l’équipe.']
      ]
    },
    nl: {
      eyebrow: 'Lokale verankering',
      title: 'Medisch vervoer in Anderlecht',
      accent: 'Anderlecht',
      subtitle: 'Vervoer op reservatie vanuit Anderlecht en in het hele Brussels Hoofdstedelijk Gewest.',
      introTitle: 'Een Brusselse dienst in Anderlecht',
      intro: ['Main Tendue Voor Allen vzw is gevestigd aan de Lenniksebaan 804/126 in 1070 Anderlecht.', 'We organiseren niet-dringend medisch en aangepast vervoer in de 19 Brusselse gemeenten.'],
      highlights: [['Adres in Anderlecht', 'De coördinatie is gevestigd aan de Lenniksebaan.'], ['19 gemeenten', 'Aanvragen uit heel het Brussels Gewest worden onderzocht.'], ['Op reservatie', 'Uren, adressen en mobiliteitsbehoeften worden vooraf gecontroleerd.']],
      usesTitle: 'Werkgebied in Brussel',
      usesIntro: 'Eén duidelijke lokale pagina voor het werkelijke gebied.',
      uses: ['Anderlecht, Brussel-Stad en Molenbeek', 'Vorst, Sint-Gillis, Ukkel en Elsene', 'Jette, Ganshoren, Koekelberg en Sint-Agatha-Berchem', 'Schaarbeek, Evere, Sint-Joost en Etterbeek', 'Oudergem en Watermaal-Bosvoorde', 'Sint-Lambrechts-Woluwe en Sint-Pieters-Woluwe'],
      detailTitle: 'Een vertrek in Anderlecht reserveren',
      detail: 'Bezorg volledig adres, toegankelijkheid, afspraakuur, bestemming en eventuele rolstoelbehoefte.',
      faq: [['Alleen in Anderlecht?', 'Nee, aanvragen uit de 19 gemeenten en sommige ritten buiten Brussel zijn mogelijk.'], ['Naar een ziekenhuis buiten Brussel?', 'Ja, na controle van haalbaarheid en tarief.'], ['Hoe bevestig ik een rit?', 'Gebruik het reservatieformulier of bel; het team bevestigt de aanvraag.']]
    }
  }
};

const commonCopy = {
  fr: {
    proof: 'Un service préparé avec vous',
    practical: 'Repères pratiques',
    approval: 'Service agréé COCOF n° 30.01.14',
    address: 'Adresse',
    hours: 'Horaires',
    hoursWeek: 'Lundi au vendredi · 7h00 à 17h30',
    hoursWeekend: 'Samedi et dimanche · 9h00 à 16h00',
    phone: 'Réservations',
    viewServices: 'Voir tous les services',
    processTitle: 'Comment organiser le transport ?',
    process: [
      ['Introduire la demande', 'Communiquez la date, les adresses, les horaires et les coordonnées du passager.'],
      ['Préciser les besoins', 'Indiquez la mobilité, le fauteuil éventuel, l’accompagnant et les contraintes d’accès.'],
      ['Recevoir la confirmation', 'L’équipe vérifie la disponibilité et confirme les modalités ainsi que le tarif applicable.']
    ],
    faq: 'Questions fréquentes',
    related: 'Services associés',
    ctaKicker: 'Une course à organiser ?',
    ctaTitle: 'Préparez votre prochain transport',
    ctaText: 'Envoyez les informations du trajet pour recevoir une estimation et une confirmation de l’équipe.',
    book: 'Demander un transport',
    call: 'Appeler Main Tendue'
  },
  nl: {
    proof: 'Een dienst die samen met u wordt voorbereid',
    practical: 'Praktische informatie',
    approval: 'COCOF-erkende dienst nr. 30.01.14',
    address: 'Adres',
    hours: 'Openingsuren',
    hoursWeek: 'Maandag tot vrijdag · 7.00 tot 17.30 uur',
    hoursWeekend: 'Zaterdag en zondag · 9.00 tot 16.00 uur',
    phone: 'Reservaties',
    viewServices: 'Alle diensten bekijken',
    processTitle: 'Hoe organiseert u het vervoer?',
    process: [
      ['Vraag indienen', 'Bezorg datum, adressen, uren en contactgegevens van de passagier.'],
      ['Behoeften verduidelijken', 'Vermeld mobiliteit, rolstoel, begeleider en toegankelijkheid.'],
      ['Bevestiging ontvangen', 'Het team controleert beschikbaarheid, voorwaarden en toepasselijk tarief.']
    ],
    faq: 'Veelgestelde vragen',
    related: 'Verwante diensten',
    ctaKicker: 'Een rit plannen?',
    ctaTitle: 'Bereid uw volgende vervoer voor',
    ctaText: 'Stuur de ritgegevens om een raming en bevestiging van het team te ontvangen.',
    book: 'Vervoer aanvragen',
    call: 'Main Tendue bellen'
  }
};

function TransportSeoPage() {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const reducedMotion = useReducedMotion();
  const lang = i18n.resolvedLanguage?.startsWith('nl') ? 'nl' : 'fr';
  const page = pageContent[pathname];
  const copy = page?.[lang] || page?.fr;
  const common = commonCopy[lang];
  const reveal = reducedMotion ? {} : {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.16 }
  };

  if (!page || !copy) return null;

  return (
    <div className="seo-service-page">
      <Banner
        title={copy.title}
        accent={copy.accent}
        subtitle={copy.subtitle}
        className="seo-service-banner"
      />

      <div className="seo-service-content">
        <section className="seo-overview" aria-labelledby="seo-overview-title">
          <motion.div className="seo-overview-copy" {...reveal} transition={{ duration: 0.5 }}>
            <span className="seo-eyebrow"><FontAwesomeIcon icon={iconByName[page.icon]} />{copy.eyebrow}</span>
            <h2 id="seo-overview-title">{copy.introTitle}</h2>
            {copy.intro.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
            <div className="seo-overview-actions">
              <Link to="/reservation" className="seo-primary-action">{common.book}<FontAwesomeIcon icon={faArrowRight} /></Link>
              <a href="tel:+32491333777" className="seo-secondary-action"><FontAwesomeIcon icon={faPhone} />+32 491 33 37 77</a>
            </div>
          </motion.div>

          <motion.aside className="seo-overview-panel" aria-label={common.practical} {...reveal} transition={{ duration: 0.55, delay: 0.06 }}>
            <header>
              <span><FontAwesomeIcon icon={faShieldAlt} /></span>
              <div><small>{common.proof}</small><strong>{common.approval}</strong></div>
            </header>
            <div className="seo-overview-panel-details">
              <div>
                <FontAwesomeIcon icon={faLocationDot} />
                <span><small>{common.address}</small><strong>Route de Lennik 804/126<br />1070 Anderlecht</strong></span>
              </div>
              <div>
                <FontAwesomeIcon icon={faClock} />
                <span><small>{common.hours}</small><strong>{common.hoursWeek}</strong><em>{common.hoursWeekend}</em></span>
              </div>
              <div>
                <FontAwesomeIcon icon={faPhone} />
                <span><small>{common.phone}</small><a href="tel:+32491333777">+32 491 33 37 77</a></span>
              </div>
            </div>
            <Link className="seo-overview-panel-link" to="/services">{common.viewServices}<FontAwesomeIcon icon={faArrowRight} /></Link>
          </motion.aside>
        </section>

        <section className="seo-highlights" aria-label={copy.eyebrow}>
          {copy.highlights.map(([title, text], index) => (
            <motion.article key={title} {...reveal} transition={{ duration: 0.45, delay: index * 0.05 }}>
              <span>0{index + 1}</span>
              <h2>{title}</h2>
              <p>{text}</p>
            </motion.article>
          ))}
        </section>

        <section className="seo-use-cases">
          <motion.div className="seo-section-heading" {...reveal} transition={{ duration: 0.5 }}>
            <span><FontAwesomeIcon icon={iconByName[page.icon]} />{copy.eyebrow}</span>
            <h2>{copy.usesTitle}</h2>
            <p>{copy.usesIntro}</p>
          </motion.div>
          <motion.ul {...reveal} transition={{ duration: 0.5, delay: 0.05 }}>
            {copy.uses.map((use) => <li key={use}><FontAwesomeIcon icon={faArrowRight} />{use}</li>)}
          </motion.ul>
        </section>

        <section className="seo-detail-band">
          <motion.div {...reveal} transition={{ duration: 0.5 }}>
            <span><FontAwesomeIcon icon={faClock} />Main Tendue</span>
            <h2>{copy.detailTitle}</h2>
          </motion.div>
          <motion.p {...reveal} transition={{ duration: 0.5, delay: 0.05 }}>{copy.detail}</motion.p>
        </section>

        <section className="seo-process">
          <motion.div className="seo-section-heading centered" {...reveal} transition={{ duration: 0.5 }}>
            <span><FontAwesomeIcon icon={faCalendarCheck} />Main Tendue</span>
            <h2>{common.processTitle}</h2>
          </motion.div>
          <div className="seo-process-grid">
            {common.process.map(([title, text], index) => (
              <motion.article key={title} {...reveal} transition={{ duration: 0.45, delay: index * 0.06 }}>
                <span>{index + 1}</span>
                <h3>{title}</h3>
                <p>{text}</p>
              </motion.article>
            ))}
          </div>
        </section>

        <section className="seo-faq-section">
          <motion.div className="seo-section-heading" {...reveal} transition={{ duration: 0.5 }}>
            <span><FontAwesomeIcon icon={faHandHoldingHeart} />Main Tendue</span>
            <h2>{common.faq}</h2>
          </motion.div>
          <div className="seo-faq-list">
            {copy.faq.map(([question, answer]) => (
              <details key={question}>
                <summary>{question}<span aria-hidden="true">+</span></summary>
                <p>{answer}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="seo-related">
          <h2>{common.related}</h2>
          <nav aria-label={common.related}>
            {page.related.map((path) => (
              <Link key={path} to={path}>
                <span>{seoPages[path][lang].breadcrumb}</span><FontAwesomeIcon icon={faArrowRight} />
              </Link>
            ))}
          </nav>
        </section>

        <section className="seo-cta">
          <div>
            <span>{common.ctaKicker}</span>
            <h2>{common.ctaTitle}</h2>
            <p>{common.ctaText}</p>
          </div>
          <div className="seo-cta-actions">
            <Link to="/reservation">{common.book}<FontAwesomeIcon icon={faArrowRight} /></Link>
            <a href="tel:+32491333777"><FontAwesomeIcon icon={faPhone} />{common.call}</a>
          </div>
        </section>
      </div>
    </div>
  );
}

export default TransportSeoPage;
